import { TagHierarchy } from './hierarchy/TagHierarchy';
import { isHierarchyEnabled } from './hierarchy/hierarchy';
import React, { useEffect, useRef, useState } from 'react';
import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import useLocalStorage from '@rio-cloud/rio-uikit/useLocalStorage';
import { FormattedMessage } from 'react-intl';
import { TagList } from './list/TagList';
import { useLocation, useNavigate } from 'react-router';
import { TagListToolbar } from './list/toolbar/TagListToolbar';
import { getSearchString } from './list/toolbar/tagListToolbarSlice';
import { TagListDetails } from './details/TagListDetails';
import { useAppDispatch, useAppSelector } from '../../../configuration/setup/hooks';
import {
    getAssignedAssetsFetchStatus,
    getAssignedDriversFetchStatus,
    getFetching,
    getSelectedTag,
    getTags,
    getTagsFetchStatus,
} from './Tags.selector';
import {
    fetchAllTags as fetchTagsThunk,
    fetchAssets as fetchAssetsThunk,
    fetchDrivers as fetchDriversThunk,
} from './tagsThunks';
import {
    defaultTagTableColumns,
    getTagTableColumnsStorageKey,
    normalizeTagTableColumns,
    type TagTableColumnConfig,
} from './list/tagListUtils';
import { getUserAccount } from '../../../configuration/login/loginSlice';
import { fetchStatus, setUnsavedChanges, tagTableRowSelected } from './tagsSlice';

export const Tags = () => {
    const fetching = useAppSelector(getFetching);
    const accountId = useAppSelector(getUserAccount);
    const tags = useAppSelector(getTags);
    const hierarchy = isHierarchyEnabled(accountId);
    const searchString = useAppSelector(getSearchString);
    const selectedTag = useAppSelector(getSelectedTag);
    const tagsFetchStatus = useAppSelector(getTagsFetchStatus);
    const assetsFetchStatus = useAppSelector(getAssignedAssetsFetchStatus);
    const driversFetchStatus = useAppSelector(getAssignedDriversFetchStatus);
    const isInitialTableLoading = tagsFetchStatus === fetchStatus.IDLE || tagsFetchStatus === fetchStatus.PENDING;

    const dispatch = useAppDispatch();
    const tableColumnsStorageKey = getTagTableColumnsStorageKey(accountId ?? null);
    const [storedColumns, setStoredColumns] = useLocalStorage<TagTableColumnConfig[]>(tableColumnsStorageKey, []);
    const previousTableColumnsStorageKey = useRef(tableColumnsStorageKey);
    const hasTableColumnsStorageKeyChanged = previousTableColumnsStorageKey.current !== tableColumnsStorageKey;
    const columns = normalizeTagTableColumns(hasTableColumnsStorageKeyChanged ? undefined : storedColumns);

    const setUnsavedTagChanges = (newValue: boolean) => dispatch(setUnsavedChanges(newValue));

    const [newRowId, setNewRowId] = useState<string | null>(null);
    const [showDiscardChangesDialog, setShowDiscardChangesDialog] = useState<boolean>(false);
    const [gridLines, setGridLines] = useState(false);
    const [compactRows, setCompactRows] = useState(false);
    const [tableResetKey, setTableResetKey] = useState(0);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (accountId && tagsFetchStatus === fetchStatus.IDLE) {
            dispatch(fetchTagsThunk({ accountId: accountId ?? null }));
        }
    }, [dispatch, accountId, tagsFetchStatus]);

    useEffect(() => {
        if (accountId && assetsFetchStatus === fetchStatus.IDLE) {
            dispatch(fetchAssetsThunk());
        }
    }, [dispatch, assetsFetchStatus, accountId]);

    useEffect(() => {
        if (accountId && driversFetchStatus === fetchStatus.IDLE) {
            dispatch(fetchDriversThunk());
        }
    }, [dispatch, driversFetchStatus, accountId]);

    useEffect(() => {
        if (!hasTableColumnsStorageKeyChanged) {
            return;
        }

        const storedValue = window.localStorage.getItem(tableColumnsStorageKey);

        previousTableColumnsStorageKey.current = tableColumnsStorageKey;
        setStoredColumns(storedValue ? JSON.parse(storedValue) : []);
    }, [hasTableColumnsStorageKeyChanged, setStoredColumns, tableColumnsStorageKey]);

    useEffect(() => {
        const normalizedStoredColumns = normalizeTagTableColumns(storedColumns);

        if (
            !hasTableColumnsStorageKeyChanged &&
            JSON.stringify(storedColumns) !== JSON.stringify(normalizedStoredColumns)
        ) {
            setStoredColumns(normalizedStoredColumns);
        }
    }, [hasTableColumnsStorageKeyChanged, setStoredColumns, storedColumns]);

    const hasTagTableContent = (() => {
        const searchTerms = searchString.toLocaleLowerCase().split(' ').filter(Boolean);

        if (searchTerms.length === 0) {
            return tags.length > 0;
        }

        return tags.some(tag => searchTerms.every(searchTerm => tag.name.toLocaleLowerCase().includes(searchTerm)));
    })();

    useEffect(() => {
        const pathWithoutBaseUrl = location.pathname.slice(6);
        if (tags.map(tag => tag.id).includes(pathWithoutBaseUrl)) {
            dispatch(tagTableRowSelected(pathWithoutBaseUrl));
        } else {
            dispatch(tagTableRowSelected(null));
        }
    }, [dispatch, tags, location]);

    const confirmDiscardChanges = () => {
        setUnsavedTagChanges(false);
        setShowDiscardChangesDialog(false);
        const pathname = newRowId ? `/tags/${newRowId}` : '/tags';
        navigate({ pathname, search: location.search });
    };

    const toggleDiscardChangesDialog = (opened: boolean, rowId: string | null) => {
        setShowDiscardChangesDialog(opened);
        setNewRowId(rowId);
    };

    const handleColumnOrderChange = (columnOrder: string[]) => {
        setStoredColumns(currentColumns => {
            const normalizedColumns = normalizeTagTableColumns(currentColumns);
            const orderedColumns = columnOrder
                .map(columnKey => normalizedColumns.find(column => column.key === columnKey))
                .filter((column): column is TagTableColumnConfig => column !== undefined);
            const missingColumns = normalizedColumns.filter(
                column => !orderedColumns.some(orderedColumn => orderedColumn.key === column.key)
            );

            return [...orderedColumns, ...missingColumns];
        });
    };

    const handleColumnVisibilityChange = (columnKey: string, visible: boolean) => {
        setStoredColumns(currentColumns =>
            normalizeTagTableColumns(currentColumns).map(column =>
                column.key === columnKey ? { ...column, visible } : column
            )
        );
    };

    const handleColumnsReset = () => {
        setStoredColumns(defaultTagTableColumns());
        setTableResetKey(currentValue => currentValue + 1);
    };

    const handleColumnResize = (columnKey: string, width?: number | string) => {
        setStoredColumns(currentColumns =>
            normalizeTagTableColumns(currentColumns).map(column => {
                if (column.key !== columnKey) {
                    return column;
                }

                if (width === undefined) {
                    const { width: _width, ...columnWithoutWidth } = column;
                    return columnWithoutWidth;
                }

                return { ...column, width };
            })
        );
    };

    const hideConfirmationDialog = () => {
        toggleDiscardChangesDialog(false, null);
    };

    return (
        <React.Fragment>
            <ConfirmationDialog
                show={showDiscardChangesDialog}
                title={<FormattedMessage id={'tags.unsavedChangesDialog.title'} />}
                content={<FormattedMessage id={'tags.unsavedChangesDialog.content'} />}
                onClickConfirm={confirmDiscardChanges}
                onClickCancel={hideConfirmationDialog}
                cancelButtonAutoTrackingKey='tags.unsavedChangesDialog.button.cancel'
                confirmButtonAutoTrackingKey='tags.unsavedChangesDialog.button.confirm'
                cancelButtonText={<FormattedMessage id={'tags.unsavedChangesDialog.button.cancel'} />}
                confirmButtonText={<FormattedMessage id={'tags.unsavedChangesDialog.button.confirm'} />}
                disableConfirm={false}
                useOverflow={false}
                bsSize={'sm'}
            />
            {selectedTag && (
                <ApplicationLayout.Sidebar className='right'>
                    <TagListDetails toggleDiscardChangesDialog={toggleDiscardChangesDialog} />
                </ApplicationLayout.Sidebar>
            )}
            <ApplicationLayout.Body className={'responsive'}>
                {fetching || isInitialTableLoading ? (
                    <Spinner text={<FormattedMessage id='tags.spinner.loading' defaultMessage='Loading tags...' />} />
                ) : hierarchy ? (
                    <TagHierarchy key={accountId} />
                ) : (
                    <>
                        <TagListToolbar
                            showAddButton={hasTagTableContent}
                            columns={columns}
                            compactRows={compactRows}
                            gridLines={gridLines}
                            onColumnOrderChange={handleColumnOrderChange}
                            onColumnVisibilityChange={handleColumnVisibilityChange}
                            onColumnsReset={handleColumnsReset}
                            onToggleCompactRows={() => setCompactRows(previousValue => !previousValue)}
                            onToggleGridLines={() => setGridLines(previousValue => !previousValue)}
                        />
                        <TagList
                            toggleDiscardChangesDialog={toggleDiscardChangesDialog}
                            columns={columns}
                            compactRows={compactRows}
                            gridLines={gridLines}
                            tableResetKey={tableResetKey}
                            onColumnOrderChange={handleColumnOrderChange}
                            onColumnResize={handleColumnResize}
                        />
                    </>
                )}
            </ApplicationLayout.Body>
        </React.Fragment>
    );
};
